// src/services/AuthService.js - FIXED WITH SECONDARY AUTH INSTANCE
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  onAuthStateChanged
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { auth, firebaseConfig } from './firebaseConfig';
import StorageService from './StorageService';

// ✅ CREATE SECONDARY AUTH INSTANCE (doesn't affect primary auth session)
let secondaryApp = null;
let secondaryAuth = null;

try {
  secondaryApp = initializeApp(firebaseConfig, 'SecondaryAuth');
  secondaryAuth = getAuth(secondaryApp);
   
} catch (error) {
  console.error('❌ Error creating secondary auth:', error);
}

const AuthService = {
  /**
   * Create a new Firebase Auth user WITHOUT signing them in
   * Uses secondary auth instance to preserve current admin session
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {object} userData - Additional user data (name, restaurantId, etc.)
   */
  async createUser(email, password, userData = {}) {
    try {
       

      // ✅ Use secondary auth instance to avoid signing out admin
      if (!secondaryAuth) {
        throw new Error('Secondary auth not initialized');
      }

      // Create Firebase Auth account with secondary auth
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth, // ✅ Use secondary auth instead of primary
        email, 
        password
      );
      const user = userCredential.user;

      // Send email verification
      await sendEmailVerification(user);
       

      // ✅ Sign out from secondary auth (doesn't affect primary admin session)
      await signOut(secondaryAuth);
       

       
      return {
        success: true,
        uid: user.uid,
        email: user.email,
        message: 'User created successfully. Verification email sent.'
      };
    } catch (error) {
      console.error('❌ Error creating user:', error);
      
      // Handle specific errors
      if (error.code === 'auth/email-already-in-use') {
        return { success: false, message: 'Email already exists' };
      } else if (error.code === 'auth/weak-password') {
        return { success: false, message: 'Password should be at least 6 characters' };
      } else if (error.code === 'auth/invalid-email') {
        return { success: false, message: 'Invalid email address' };
      }
      
      return { success: false, message: error.message };
    }
  },

  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   */
  async signIn(email, password) {
    try {
       

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Check if email is verified
      if (!user.emailVerified) {
        console.warn('⚠️ Email not verified');
        return {
          success: false,
          message: 'Please verify your email before logging in. Check your inbox.',
          needsVerification: true
        };
      }

       
      return {
        success: true,
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified
      };
    } catch (error) {
      console.error('❌ Sign in error:', error);
      
      if (error.code === 'auth/user-not-found') {
        return { success: false, message: 'User not found' };
      } else if (error.code === 'auth/wrong-password') {
        return { success: false, message: 'Incorrect password' };
      } else if (error.code === 'auth/invalid-email') {
        return { success: false, message: 'Invalid email address' };
      } else if (error.code === 'auth/invalid-credential') {
        return { success: false, message: 'Invalid email or password' };
      }
      
      return { success: false, message: 'Login failed. Please try again.' };
    }
  },

  /**
   * Sign out current user
   */
  async signOut() {
    try {
      await signOut(auth);
       
      return { success: true };
    } catch (error) {
      console.error('❌ Sign out error:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Send password reset email
   * @param {string} email - User email
   */
  async sendPasswordResetEmail(email) {
    try {
       
      
      await sendPasswordResetEmail(auth, email);
      
       
      return {
        success: true,
        message: 'Password reset email sent. Please check your inbox.'
      };
    } catch (error) {
      console.error('❌ Password reset error:', error);
      
      if (error.code === 'auth/user-not-found') {
        return { success: false, message: 'No account found with this email' };
      } else if (error.code === 'auth/invalid-email') {
        return { success: false, message: 'Invalid email address' };
      }
      
      return { success: false, message: 'Failed to send reset email. Please try again.' };
    }
  },

  /**
   * Update password for current user
   * @param {string} currentPassword - Current password for verification
   * @param {string} newPassword - New password
   */
  async updatePassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;
      
      if (!user) {
        return { success: false, message: 'No user logged in' };
      }

      // Re-authenticate user before password change
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);
      
       
      return { success: true, message: 'Password updated successfully' };
    } catch (error) {
      console.error('❌ Password update error:', error);
      
      if (error.code === 'auth/wrong-password') {
        return { success: false, message: 'Current password is incorrect' };
      } else if (error.code === 'auth/weak-password') {
        return { success: false, message: 'New password should be at least 6 characters' };
      }
      
      return { success: false, message: 'Failed to update password' };
    }
  },

  /**
   * Resend email verification
   */
  async resendVerificationEmail() {
    try {
      const user = auth.currentUser;
      
      if (!user) {
        return { success: false, message: 'No user logged in' };
      }

      await sendEmailVerification(user);
      
       
      return { success: true, message: 'Verification email sent' };
    } catch (error) {
      console.error('❌ Error resending verification:', error);
      return { success: false, message: 'Failed to send verification email' };
    }
  },

  /**
   * Get current authenticated user
   */
  getCurrentUser() {
    return auth.currentUser;
  },

  /**
   * Listen to auth state changes
   * @param {function} callback - Callback function with user parameter
   */
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return auth.currentUser !== null;
  },

  /**
   * Get current user's UID
   */
  getCurrentUserId() {
    return auth.currentUser?.uid || null;
  },

  /**
   * Get current user's email
   */
  getCurrentUserEmail() {
    return auth.currentUser?.email || null;
  }
};

export default AuthService;